'use client';

import { useGameStore } from '@/store/useGameStore';
import { getBadgeRarityColor } from '@/lib/gamification';
import { formatDate } from '@/lib/utils';
import type { Badge } from '@/types';

interface Props {
  badges: Badge[];
}

export function BadgeGrid({ badges }: Props) {
  const unlockedBadges = useGameStore((s) => s.unlockedBadges);
  const badgeUnlockDates = useGameStore((s) => s.badgeUnlockDates);

  return (
    <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
      {badges.map((badge) => {
        const unlocked = unlockedBadges.includes(badge.id);
        const unlockDate = badgeUnlockDates[badge.id];
        const rarityColor = getBadgeRarityColor(badge.rarity);

        return (
          <div
            key={badge.id}
            className="relative rounded-xl p-4 text-center group transition-all"
            style={{
              background: unlocked ? `${rarityColor}10` : 'var(--color-surface)',
              border: `1px solid ${unlocked ? `${rarityColor}40` : 'var(--color-border)'}`,
              filter: unlocked ? 'none' : 'grayscale(1)',
              opacity: unlocked ? 1 : 0.6,
            }}
            title={unlocked ? `Unlocked: ${formatDate(unlockDate)}` : badge.condition}
          >
            {/* Lock icon for locked */}
            {!unlocked && (
              <div className="absolute inset-0 flex items-center justify-center rounded-xl opacity-0 group-hover:opacity-100 transition-opacity" style={{ background: 'rgba(8,8,16,0.7)' }}>
                <span className="text-xs text-center px-2" style={{ color: 'var(--color-text-secondary)' }}>
                  {badge.condition}
                </span>
              </div>
            )}

            <span className="text-3xl block mb-2" role="img" aria-label={badge.name}>
              {badge.icon}
            </span>
            <p className="text-xs font-semibold leading-tight mb-1" style={{ color: unlocked ? 'var(--color-text-primary)' : 'var(--color-text-secondary)' }}>
              {badge.name}
            </p>
            <span
              className="text-xs px-1.5 py-0.5 rounded-full capitalize"
              style={{
                background: `${rarityColor}20`,
                color: rarityColor,
              }}
            >
              {badge.rarity}
            </span>
            {unlocked && unlockDate && (
              <p className="text-xs mt-1.5" style={{ color: 'var(--color-text-muted)' }}>
                {formatDate(unlockDate)}
              </p>
            )}
          </div>
        );
      })}
    </div>
  );
}
